import React, { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import ChartDataModal from '../chart-data-modal'
import type { KrChartContextType } from '@workboard/ui-okr';
import { canShowRagBands, numberFormatter } from '@workboard/ui-okr';
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import moment from 'moment';

type KrGraphMenuProps = {
  graphHandler: any;
} | {
  enhancedChartContext: KrChartContextType
}

const StyledTableCell = styled(TableCell)({
  border: "1px solid rgb(224, 224, 224)",
  padding: '.5em',
  textAlign: "left",
  [`&.${tableCellClasses.head}`]: {},
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
});

const StyledHeaderText = styled('span')({
  fontWeight: 'bold',
})

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const DataTable = ({ enhancedChartContext }: { enhancedChartContext: KrChartContextType }) => {
  const { t } = useTranslation();
  const { kr } = enhancedChartContext;
  const { chartData = [], valueType } = kr ?? {};
  const hasMilestones = kr?.milestones && kr?.milestones.length > 0;
  const upperLowerLimit = !!kr?.upperLimit || !!kr?.lowerLimit;

  return (
    <TableContainer>
      <Table className="highcharts-data-table" sx={{ minWidth: 400 }}>
        <TableHead>
          <TableRow>
            <StyledTableCell colSpan={upperLowerLimit ? 5 : 4} sx={{ background: 'none' }}><StyledHeaderText>{t('Chart')}</StyledHeaderText></StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <StyledTableRow>
            <StyledTableCell><StyledHeaderText>{t('Date')}</StyledHeaderText></StyledTableCell>
            <StyledTableCell><StyledHeaderText>{t('Updates')}</StyledHeaderText></StyledTableCell>
            <StyledTableCell><StyledHeaderText>{t('Actual')}</StyledHeaderText></StyledTableCell>
            {upperLowerLimit ? (
              <>
                <StyledTableCell><StyledHeaderText>{t('Lower Limit')}</StyledHeaderText></StyledTableCell>
                <StyledTableCell><StyledHeaderText>{t('Upper Limit')}</StyledHeaderText></StyledTableCell>
              </>
            ) : (
              <StyledTableCell><StyledHeaderText>{hasMilestones ? t('Expected') : t('Target')}</StyledHeaderText></StyledTableCell>
            )}
          </StyledTableRow>
          {chartData.map((d) => (
            <StyledTableRow key={d.date}>
              <StyledTableCell>
                {moment(d.date).utc().format('MMM D, YYYY')}
              </StyledTableCell>
              <StyledTableCell>{numberFormatter(d.inputValue, { valueType })}</StyledTableCell>
              <StyledTableCell>{numberFormatter(d.runningTotal, { valueType })}</StyledTableCell>
              {upperLowerLimit ? (
                <>
                  <StyledTableCell>{numberFormatter(d.lowerLimit, { valueType })}</StyledTableCell>
                  <StyledTableCell>{numberFormatter(d.upperLimit, { valueType })}</StyledTableCell>
                </>
              ) : (
                <StyledTableCell>
                  {numberFormatter(hasMilestones ? d.expectedValue : d.targetValue, { valueType })}
                </StyledTableCell>
              )}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

type MenuOption =
  | "viewFullscreen"
  | "printChart"
  | "dataTable"
  | "downloadPNG"
  | "downloadJPEG"
  | "downloadPDF"
  | "downloadSVG"
  | "downloadCSV"
  | "downloadXLS"
  | "togglePrediction"
  | "toggleRagBands";

export const KrGraphMenu = memo((props: KrGraphMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();
  const open = Boolean(anchorEl);
  const graphHandler = 'graphHandler' in props ? props.graphHandler : null
  const enhancedChartContext = 'enhancedChartContext' in props ? props.enhancedChartContext : null
  const ragBandsAvailable = !!enhancedChartContext
    && canShowRagBands(enhancedChartContext.kr, enhancedChartContext.aggregation)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const viewDataTable = () => {
    setShowModal(true);

    if (graphHandler) graphHandler('graph-table-view-id')
  };

  const handleOptionClick = (option: MenuOption) => {
    const chart = enhancedChartContext?.chartRef?.chart as any;

    switch (option) {
      case "togglePrediction":
        enhancedChartContext?.togglePrediction?.();
        break;
      case "toggleRagBands":
        enhancedChartContext?.toggleRagBands?.();
        break;
      case "viewFullscreen":
        chart?.fullscreen?.toggle?.();
        break;
      case "printChart":
        chart?.print?.();
        break;
      case "dataTable":
        viewDataTable?.();
        break;
      case "downloadPNG":
        chart?.exportChartLocal?.(
          {
            type: "image/png",
          },
          {}
        );
        break;
      case "downloadJPEG":
        chart?.exportChartLocal?.(
          {
            type: "image/jpeg",
          },
          {}
        );
        break;
      case "downloadPDF":
        chart?.exportChartLocal?.(
          {
            type: "application/pdf",
          },
          {}
        );
        break;
      case "downloadSVG":
        chart?.exportChartLocal?.(
          {
            type: "image/svg+xml",
          },
          {}
        );
        break;
      case "downloadCSV":
        chart?.downloadCSV?.();
        break;
      case "downloadXLS":
        chart?.downloadXLS?.();
        break;
    }
    handleClose();
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        id="kr-graph-menu-button"
        aria-controls={open ? 'kr-graph-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MenuIcon />
      </IconButton>
      <Menu
        id="kr-graph-menu"
        MenuListProps={{
          'aria-labelledby': 'kr-graph-menu-button',
        }}
        variant='menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              width: enhancedChartContext ? '32ch' : '20ch',
            },
          },
        }}
      >
        {
          enhancedChartContext &&
          (
            <>
              {!!enhancedChartContext.kr?.prediction && (
                <MenuItem divider={!ragBandsAvailable} onClick={() => handleOptionClick("togglePrediction")} dense>
                  {enhancedChartContext.showPrediction ? t('Hide prediction') : t('Show prediction')}
                </MenuItem>
              )}
              {ragBandsAvailable && (
                <MenuItem divider onClick={() => handleOptionClick("toggleRagBands")} dense>
                  {enhancedChartContext.showRagBands ? t('Hide RAG bands') : t('Show RAG bands')}
                </MenuItem>
              )}
              <MenuItem onClick={() => handleOptionClick("printChart")} dense>
                {t('Print chart')}
              </MenuItem>
              <MenuItem onClick={() => handleOptionClick("downloadPNG")} dense>
                {t('Download PNG image')}
              </MenuItem>
              <MenuItem onClick={() => handleOptionClick("downloadJPEG")} dense>
                {t('Download JPEG image')}
              </MenuItem>
              <MenuItem onClick={() => handleOptionClick("downloadPDF")} dense>
                {t('Download PDF document')}
              </MenuItem>
              <MenuItem onClick={() => handleOptionClick("downloadSVG")} dense>
                {t('Download SVG vector image')}
              </MenuItem>
              <MenuItem onClick={() => handleOptionClick("downloadCSV")} dense>
                {t('Download CSV')}
              </MenuItem>
              <MenuItem divider onClick={() => handleOptionClick("downloadXLS")} dense>
                {t('Download XLS')}
              </MenuItem>
              <MenuItem title={t('Data Table')} onClick={() => handleOptionClick("dataTable")} dense>
                {t('View data table')}
              </MenuItem>
            </>
          )
        }
        {!enhancedChartContext && (
          <MenuItem title={t('Data Table')} onClick={() => handleOptionClick("dataTable")} dense>
            {t('View data table')}
          </MenuItem>
        )}
      </Menu>
      {showModal && <ChartDataModal callBackHandler={graphHandler} showModal={showModal} setShowModal={setShowModal}>
        {enhancedChartContext && <DataTable enhancedChartContext={enhancedChartContext} />}
      </ChartDataModal>}
    </div>
  );
})
